import React from 'react';
import { cn } from '../../shared/util/cn';

export interface InputProps extends React.InputHTMLAttributes<HTMLInputElement> {
  error?: boolean;
  helperText?: string;
  label?: string;
  /**
   * Extra classes for the inner <input> element.
   * The standard `className` prop applies to the field wrapper (so layout
   * classes like `mt-3` create space between fields, not between label and input).
   */
  inputClassName?: string;
}

export const Input = React.forwardRef<HTMLInputElement, InputProps>(
  ({ className, inputClassName, error, helperText, label, id, ...props }, ref) => {
    const inputId = id || `input-${Math.random().toString(36).substr(2, 9)}`;

    return (
      <div className={cn('w-full', className)}>
        {label && (
          <label
            htmlFor={inputId}
            className="mb-1 block text-sm font-semibold text-[var(--color-mockup-text)]"
          >
            {label}
          </label>
        )}
        <input
          id={inputId}
          className={cn(
            'flex h-10 w-full rounded-2xl border-2 border-[var(--color-mockup-border)] bg-surface px-4 py-2 text-sm',
            'ring-offset-surface file:border-0 file:bg-transparent file:text-sm file:font-medium',
            'placeholder:text-[var(--color-mockup-text-light)]',
            'focus:outline-none focus:border-primary focus:shadow-[0_4px_12px_rgba(18,181,170,0.15)] transition-all duration-200',
            'disabled:cursor-not-allowed disabled:opacity-50',
            'dark:border-[var(--color-mockup-border)] dark:placeholder:text-[var(--color-mockup-text-light)]',
            error && 'border-[var(--color-mockup-coral)] focus:border-[var(--color-mockup-coral)] focus:shadow-[0_4px_12px_rgba(255,107,91,0.15)]',
            inputClassName
          )}
          ref={ref}
          {...props}
        />
        {helperText && (
          <p className={cn(
            'mt-1 text-sm',
            error ? 'text-[var(--color-mockup-coral)]' : 'text-[var(--color-mockup-text-mid)]'
          )}>
            {helperText}
          </p>
        )}
      </div>
    );
  }
);

Input.displayName = 'Input';
